/**
 * Campaign Page Header
 *
 * Consistent header component used across all campaign builder pages.
 */

import { Button } from '@/components/ui/button'
import { ArrowLeft } from 'lucide-react'
import { useEffect } from 'react'

interface CampaignPageHeaderProps {
    title: React.ReactNode
    subtitle: string
    onBack: () => void
    /** Optional label for the back button (default: "Back"). */
    backLabel?: string
    onContinue?: () => void
    continueLabel?: string
    /** Optional short line under the continue button (e.g. "Saves sections and starts copy…") */
    continueSubtext?: string
    continueDisabled?: boolean
    showContinue?: boolean
    continueTestId?: string
    children?: React.ReactNode
    /** When false, header scrolls with the page (e.g. inside a parent sticky stack). Default true. */
    sticky?: boolean
}

export function CampaignPageHeader({
    title,
    subtitle,
    onBack,
    backLabel = 'Back',
    onContinue,
    continueLabel = 'Continue →',
    continueSubtext,
    continueDisabled = false,
    showContinue = true,
    continueTestId,
    children,
    sticky = true,
}: CampaignPageHeaderProps) {
    // 🆕 Auto-scroll to top on every campaign page load
    useEffect(() => {
        window.scrollTo(0, 0)
        // Also find and scroll the specific dashboard scroll container
        const scrollContainer = document.querySelector('.overflow-auto')
        if (scrollContainer) {
            scrollContainer.scrollTop = 0
        }
    }, [title]) // Trigger on title change (page change)

    return (
        <header
            className={`bg-white border-b-4 border-magenta-vivid shadow-sm ${
                sticky ? 'sticky top-0 z-40' : 'relative z-0'
            }`}
        >
            <div className='px-4 sm:px-6 py-3 sm:py-4 min-w-0'>
                {/* On mobile: title row stacks above action row to avoid cramped single line */}
                <div className='min-w-0 mb-2 sm:hidden'>
                    <h1 className='text-base font-bold text-violet-base break-words leading-tight'>{title}</h1>
                    <p className='text-xs text-gray-500 mt-0.5 break-words'>{subtitle}</p>
                </div>
                <div className='flex flex-wrap items-center justify-between gap-2 sm:gap-3 min-w-0'>
                    <div className='min-w-0 flex-1 hidden sm:block'>
                        <h1 className='text-xl font-bold text-violet-base break-words'>{title}</h1>
                        <p className='text-xs text-gray-500 mt-1 break-words'>{subtitle}</p>
                    </div>
                    <div className='flex items-center gap-2 sm:gap-3 shrink-0 flex-wrap justify-end w-full sm:w-auto'>
                        {children}
                        <Button variant='outline' onClick={onBack} className='gap-2 min-h-[44px]'>
                            <ArrowLeft className='w-4 h-4' />
                            {backLabel}
                        </Button>
                        {showContinue && onContinue && (
                            <div className='flex flex-col items-end'>
                                <Button
                                    onClick={onContinue}
                                    disabled={continueDisabled}
                                    variant={continueDisabled ? 'outline' : 'default'}
                                    size='lg'
                                    className='min-h-[44px]'
                                    data-testid={continueTestId}
                                >
                                    {continueLabel}
                                </Button>
                                {continueSubtext && (
                                    <span className='text-xs text-gray-500 mt-1 max-w-[220px] text-right'>{continueSubtext}</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    )
}
